// Grids & Gantts — the timeline maths for the structure's Gantt.
//
// WHY THIS EXISTS RATHER THAN THE SHARED Gantt.jsx. That component draws
// every row it is given. That is right for a page with a few hundred rows
// and fatal here: this structure opens onto twenty-eight thousand, and the
// grid only survives them by windowing — about thirty rows in the DOM at a
// time. Handing the whole array to a component that renders all of it would
// undo the one thing keeping the page alive.
//
// So the timeline lives INSIDE the grid instead: same scroller, same
// windowed row list, same renderer. A row's bar cannot drift out of line
// with its own cells, because they are the same row. Opening a folder opens
// it in both at the same instant for the same reason.
//
// Everything here is pure — the window, the scale, the ticks, the bar for
// one row — so it runs under node in the suite.

import { addDays, bucketsBetween, daysBetween, ganttGrain, labelOf } from '../shared/interval';

// Date arithmetic moved to the shared interval module, where the bar-drag
// maths needs it too. Re-exported so the suite and the grid keep their one
// import for "the timeline's numbers".
export { addDays };

// Pixels per DAY at each grain, and the numbers are not arbitrary — each one
// answers "what does this grain's own LABEL need".
//
//   day    26px, so "24 Sep" fits in a column
//   week   10px → 70px a week, which fits "wk 39" AND leaves a one-day slip
//          visible as seven pixels of movement
//   month  3.4px → ~103px a month, which fits "Sep 26"
//
// Weeks was 7px (49px a column) and months 2.6px (78px), and at both the
// labels collided into a grey smear. A tick nobody can read is a tick that is
// not there, and a Gantt with no axis is a row of coloured sticks.
//
// Quarters and years stay because `windowOf` and `ticksOf` are general, but a
// timeline does not OFFER them — see GANTT_GRAINS in shared/interval.
export const PX = { day: 26, week: 10, month: 3.4, quarter: 1.1, year: 0.42 };

const pad = (n) => String(n).padStart(2, '0');
export const todayIso = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

// The span the chart covers: every date in the rows, TODAY always included
// (a plan entirely in the past still wants to show how far past it is), and
// a week of air either side so a bar at the edge is not clipped.
// ONE PASS, NO SORT. This used to push every date of every row into an array
// and sort it, to read the two ends. On a structure of twenty-eight thousand
// that is a fifty-six thousand element sort, run again every time the rows
// change — a filter, an opened folder, a saved edit. Min and max are one
// pass and no allocation, and they are the only two things anybody wanted.
export function windowOf(rows, { now = new Date() } = {}) {
  const today = todayIso(now);
  let min = '';
  let max = '';
  const see = (d) => {
    if (!d) return;
    if (!min || d < min) min = d;
    if (!max || d > max) max = d;
  };
  for (let i = 0; i < (rows || []).length; i += 1) {
    const r = rows[i];
    see(r.start);
    see(r.due);
  }
  const lo = min && min < today ? min : today;
  const hi = max && max > today ? max : today;
  const from = addDays(lo, -7);
  const to = addDays(hi, 14);
  return {
    from, to, today,
    days: Math.max(1, (daysBetween(from, to) || 0) + 1),
    spanDays: min ? (daysBetween(min, max) || 0) : 0,
  };
}

// THE READER PICKS THE GRAIN, and until they do it is Weeks.
//
// It used to be picked FOR them, from the span of the data — which meant the
// axis changed under them: open a folder holding one long job and a chart
// they were reading in days became a chart in quarters, same page, one click.
// A plan is read in weeks. If a reader wants days they say so, and it sticks.
//
// `win` is still taken so no caller changes, and so a saved grain can be
// reasoned about per-window if it ever should be.
export const grainFor = (win, chosen) => ganttGrain(chosen);

// ---- the two tiers of an axis ------------------------------------------------
//
// A date column carries TWO facts and only one of them changes often. Printing
// both in every column ("2026 wk 39", "24 Sep") spends the width on the half
// that is the same for thirty columns running, and then truncates the half
// that is not. So the axis is two rows: the slow fact merged across the span
// it covers, and the fast one in its own column.
//
//   days    the MONTH merged above, the day number below
//   weeks   the YEAR merged above, CW39 below
//   months  the YEAR merged above, the month below
//
// CW is what a plan is actually discussed in on this client's floor —
// "CW39", not "week 39" and not "2026 wk 39".
export const superGrainOf = (grain) => (grain === 'day' ? 'month' : 'year');

// What a tick says in the BOTTOM row, where the tier above has already said
// the year (or the month).
export function tickLabel(key, grain) {
  const s = String(key || '');
  if (grain === 'week') return `CW${s.slice(s.indexOf('-W') + 2)}`;
  if (grain === 'day') return String(Number(s.slice(8, 10)) || s);
  return labelOf(key, grain);
}

// The merged row above it: the same arithmetic as `ticksOf`, at the slower
// grain. `tickLabel` falls through to the shared vocabulary for months and
// years, which is exactly the full label this row exists to print.
export const superTicks = (win, grain, px) => ticksOf(win, superGrainOf(grain), px);

// Saturday and Sunday. A plan is read against the working week, and a bar
// that appears to run for ten days when four of them are a weekend is a bar
// that will be argued about. UTC, like every other date here.
export function isWeekend(isoDate) {
  const s = String(isoDate || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return false;
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 || dow === 6;
}

// The weekend stripes, at day grain only — at weeks and months a weekend is
// narrower than a hairline and shading it just dirties the chart.
export function weekendBands(win, grain, px) {
  if (grain !== 'day' || !(px > 0)) return [];
  const out = [];
  const n = win.days;
  for (let i = 0; i < n; i += 1) {
    const iso = addDays(win.from, i);
    if (!isWeekend(iso)) continue;
    const last = out[out.length - 1];
    // Saturday and Sunday are ONE band, not two — two abutting rectangles
    // with a shared edge render a seam at fractional zoom.
    if (last && last.to === i - 1) { last.to = i; last.w += px; continue; }
    out.push({ key: iso, x: i * px, w: px, to: i });
  }
  return out;
}

// The column stripes and their labels, at the chosen grain.
export function ticksOf(win, grain, px) {
  const keys = bucketsBetween(win.from, win.to, grain);
  return keys.map((key, i) => {
    // A bucket starts where the previous one ended; the first starts at the
    // window's own edge, which may be mid-bucket.
    const next = keys[i + 1];
    const startIso = i === 0 ? win.from : bucketStart(key, grain);
    const endIso = next ? bucketStart(next, grain) : addDays(win.to, 1);
    const x = (daysBetween(win.from, startIso) || 0) * px;
    const w = Math.max(1, ((daysBetween(startIso, endIso) || 0)) * px);
    // `weekend` is only ever true at day grain — the header greys those
    // columns, and the backdrop bands them, from the same one fact.
    return { key, x, w, label: tickLabel(key, grain), startIso, endIso, weekend: grain === 'day' && isWeekend(key) };
  });
}

// The first calendar day of a bucket key ("2026-W39", "2026-Q3", "2026-09").
export function bucketStart(key, grain) {
  const s = String(key || '');
  if (grain === 'day') return s;
  if (grain === 'year') return `${s}-01-01`;
  if (grain === 'quarter') {
    const [y, q] = s.split('-Q');
    return `${y}-${pad((Number(q) - 1) * 3 + 1)}-01`;
  }
  if (grain === 'week') {
    const [y, w] = s.split('-W').map(Number);
    // ISO week 1 contains 4 January; walk back to its Monday.
    const jan4 = new Date(Date.UTC(y, 0, 4));
    const dow = (jan4.getUTCDay() + 6) % 7;
    const monday = new Date(Date.UTC(y, 0, 4 - dow + (w - 1) * 7));
    return `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`;
  }
  return `${s}-01`;
}

// What to draw for one row.
//
//   bar   start and due — the work has a span
//   dot   ONE of them — a diamond, because a zero-length bar reads as
//         missing data when it is not
//   null  neither — nothing to draw, and no pretending otherwise
//
// `inverted` marks a due date BEFORE its start date. That is a data problem
// worth seeing, not a reason to refuse to draw: the bar is drawn between
// the two and flagged.
export function barOf(row, win, px) {
  const a = row && row.start;
  const b = row && row.due;
  if (!a && !b) return null;
  if (a && b) {
    const [s, e] = a <= b ? [a, b] : [b, a];
    return {
      kind: 'bar',
      x: (daysBetween(win.from, s) || 0) * px,
      w: Math.max(px, ((daysBetween(s, e) || 0) + 1) * px),
      from: s, to: e, inverted: a > b,
    };
  }
  const d = b || a;
  return { kind: 'dot', x: (daysBetween(win.from, d) || 0) * px, w: px, from: d, to: d, onlyStart: !b };
}

// A BAR STAYS INSIDE THE CHART. A folder's span is the span of everything
// beneath it, shut rows included, and the window is read off the rows on
// screen — so an aggregate can begin before the window does, or end after
// it. Drawn as computed, that bar ran leftwards under the columns (on paper,
// straight across the Status and Due date cells; on screen, under the frozen
// pane). The dates are still the row's dates; what is DRAWN stops at the
// chart's edges, and says on which side it was cut.
//
//   null      the whole bar is outside the chart — nothing to draw
//   cutStart  the bar began before the chart's left edge
//   cutEnd    it ends past the right edge
export function clipBar(bar, chartW) {
  if (!bar) return null;
  const w = Number(chartW);
  if (!(w > 0)) return bar;
  const x0 = bar.x;
  const x1 = bar.x + bar.w;
  if (x1 <= 0 || x0 >= w) return null;
  const left = Math.max(0, x0);
  const right = Math.min(w, x1);
  return { ...bar, x: left, w: Math.max(0, right - left), cutStart: x0 < 0, cutEnd: x1 > w };
}

// Blue on the plan, red past its due date, green done. A row's colour is a
// fact about the work, so it is decided here and not in the renderer.
export function toneOf(row, today) {
  if (row && row.done) return 'done';
  if (row && row.due && row.due < today) return 'late';
  return 'planned';
}

// The overrun: from the due date to today, drawn as a tail so lateness has
// a LENGTH rather than just a colour.
export function tailOf(row, bar, win, px, today) {
  if (!row || row.done || !row.due || row.due >= today || !bar) return null;
  const todayX = (daysBetween(win.from, today) || 0) * px;
  const from = bar.x + bar.w;
  const w = todayX - from;
  return w > 0 ? { x: from, w } : null;
}

export const todayX = (win, px) => (daysBetween(win.from, win.today) || 0) * px;
export const chartWidth = (win, px) => Math.max(240, win.days * px);
